import {
  useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import { isNullsy } from '~/_shared/utils/typeGuards';
import { AppErrorType } from '~/appError/appErrorType.enum';
import { useGetBoundaryNameOverrides } from '~/boundary/useGetBoundaryNameOverrides';
import {
  getBoundaryTerritoryAssignments, type GetBoundaryTerritoryAssignmentsRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { useBoundaryTerritoryGroupDefinition } from './useBoundaryTerritoryGroupDefinition';

type BoundaryTerritoryAssignmentRow = Readonly<{
  display_name: string;
  bt_display_name: string;
}>;

const escapeCsvValue = (value: string) => (
  /["\r\n,]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
);

const convertAssignmentsToCsv = (
  assignments: ReadonlyArray<BoundaryTerritoryAssignmentRow>,
  header?: readonly [string, string],
) => [
  ...(header ? [header] : []),
  ...assignments.map(assignment => [assignment.display_name, assignment.bt_display_name] as const),
]
  .map(row => row.map(escapeCsvValue).join(','))
  .join('\n');

export const useGetBoundaryTerritoryAssignments = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const getBTGDefinition = useBoundaryTerritoryGroupDefinition();
  const { getBoundaryNameOverrides } = useGetBoundaryNameOverrides();
  const spreadsheetId = usePrimarySpreadsheetId();

  const [isLoading, setIsLoading] = useState(false);

  const getBTGAssignments = useCallback(async ({
    boundaryTerritoryGroupId,
    includeNoLocations,
    fileType,
  }: Readonly<{
    boundaryTerritoryGroupId: number;
    includeNoLocations?: boolean;
    fileType?: GetBoundaryTerritoryAssignmentsRequest['file_type'];
  }>) => {
    const definition = getBTGDefinition({ boundaryTerritoryGroupId });

    if (!definition || isNullsy(clientId) || isNullsy(spreadsheetId)) {
      return undefined;
    }

    setIsLoading(true);

    try {
      const nameOverrides = await getBoundaryNameOverrides(definition.boundary_group_id);

      const assignments = await getBoundaryTerritoryAssignments(clientId, {
        definition,
        name_overrides: nameOverrides,
        include_no_locations: includeNoLocations,
        file_type: fileType,
        spreadsheet_id: spreadsheetId,
      });

      setIsLoading(false);
      return assignments;
    }
    catch (error) {
      dispatch(createAppError({
        type: AppErrorType.General,
        title: t('Error exporting data'),
        content: t('Please try again. If this problem persists, please contact the customer support.'),
      }));
    }

    setIsLoading(false);

    return undefined;
  }, [clientId, dispatch, getBTGDefinition, getBoundaryNameOverrides, t, spreadsheetId]);

  const exportBoundaryTerritoryGroupAssignmentsAsFile = useCallback(async ({
    boundaryTerritoryGroupId,
    includeNoLocations,
    fileType,
  }: Readonly<{
    boundaryTerritoryGroupId: number;
    includeNoLocations?: boolean;
    fileType: 'xlsx' | 'csv' | 'tsv';
  }>) => {
    const assignments = await getBTGAssignments({ boundaryTerritoryGroupId, includeNoLocations, fileType });
    return assignments?.download;
  }, [getBTGAssignments]);

  const exportBoundaryTerritoryGroupAssignmentsAsString = useCallback(async ({
    boundaryTerritoryGroupId,
    includeNoLocations,
  }: Readonly<{
    boundaryTerritoryGroupId: number;
    includeNoLocations?: boolean;
  }>) => {
    const assignments = await getBTGAssignments({ boundaryTerritoryGroupId, includeNoLocations });

    if (!assignments?.data) {
      return undefined;
    }

    return convertAssignmentsToCsv(assignments.data);
  }, [getBTGAssignments]);

  return {
    getBTGAssignments,
    exportBoundaryTerritoryGroupAssignmentsAsFile,
    exportBoundaryTerritoryGroupAssignmentsAsString,
    isLoading,
  };
};
